/** Renders the persistent public chrome around route-level portfolio pages. */
import { useEffect } from 'react';
import { BrowserRouter, useLocation } from 'react-router-dom';
import { AppProviders } from './providers/AppProviders';
import { AppRouter } from './router';
import { Footer } from '../presentation/components/layout/Footer';
import { Header } from '../presentation/components/layout/Header';
import { PwaInstallPrompt } from '../presentation/components/ui/PwaInstallPrompt';
import { ScrollToTop } from '../presentation/components/layout/ScrollToTop';
import { AppearanceControl } from '../features/theme/presentation/components/AppearanceControl';
import { usePortfolio } from '../features/portfolio/presentation/context/PortfolioContext';

/** Synchronizes browser metadata with the Firestore-managed SEO document. */
function SeoManager() {
  const { data } = usePortfolio();

  useEffect(() => {
    document.title = data.siteContent.seo.title;
    const description = document.querySelector<HTMLMetaElement>('meta[name="description"]');
    description?.setAttribute('content', data.siteContent.seo.description);
  }, [data.siteContent.seo]);

  return null;
}

function PublicAppFrame() {
  return (
    <>
      <SeoManager />
      <ScrollToTop />
      <Header />
      <main id="main-content">
        <AppRouter />
      </main>
      <Footer />
      <AppearanceControl />
      <PwaInstallPrompt />
    </>
  );
}

/** Keeps URL-only admin pages completely outside the public portfolio chrome. */
function AppFrame() {
  const location = useLocation();
  const normalizedPath = location.pathname.replace(/\/+$/, '') || '/';
  const isAdminRoute = normalizedPath === '/login' || normalizedPath === '/db';

  if (isAdminRoute) {
    return (
      <main id="main-content" className="admin-main-content">
        <AppRouter />
      </main>
    );
  }

  return <PublicAppFrame />;
}

export default function App() {
  return (
    <BrowserRouter>
      <AppProviders>
        <AppFrame />
      </AppProviders>
    </BrowserRouter>
  );
}
